import { Stack, Text } from "@assetwatch/ui";
import { useTranslation } from "react-i18next";
import type { EnterpriseAssetsV2SummaryResponse } from "../../api/enterpriseClient";
import { formatAssetsV2Count } from "../../utils";

export function AssetsV2HighRiskBar({
  summary,
}: {
  summary: EnterpriseAssetsV2SummaryResponse["summary"];
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const percentageNumberFormat = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const highRiskPercentage = Math.max(
    0,
    Math.min(100, summary.highRiskAssetPercentage),
  );
  const notHighRiskPercentage = Math.max(
    0,
    Math.min(100, summary.notHighRiskAssetPercentage),
  );

  return (
    <Stack>
      <div
        className="flex h-(--spacing-24) overflow-hidden rounded-full bg-surface-container-high"
        role="img"
        aria-label={t("enterprise.assetsV2.riskChartAriaLabel", {
          highRisk: formatAssetsV2Count(summary.highRiskAssetCount, locale),
          highRiskPercentage: percentageNumberFormat.format(highRiskPercentage),
          notHighRisk: formatAssetsV2Count(
            summary.notHighRiskAssetCount,
            locale,
          ),
          notHighRiskPercentage: percentageNumberFormat.format(
            notHighRiskPercentage,
          ),
        })}
      >
        {summary.monitoredAssetCount > 0 && highRiskPercentage > 0 ? (
          <div
            className="flex min-w-0 items-center justify-center bg-datavis-crit-yellow text-text-default"
            style={{ width: `${highRiskPercentage}%` }}
            aria-hidden="true"
          />
        ) : null}
        {summary.monitoredAssetCount > 0 && notHighRiskPercentage > 0 ? (
          <div
            className="flex min-w-0 items-center justify-center bg-datavis-crit-green text-text-default"
            style={{ width: `${notHighRiskPercentage}%` }}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-(--spacing-8) sm:grid-cols-2">
        <Text size="md">
          <span
            className="mr-(--spacing-8) inline-block size-(--spacing-12) rounded-full bg-datavis-crit-yellow align-middle"
            aria-hidden="true"
          />
          {t("enterprise.assetsV2.highRiskLegend", {
            count: formatAssetsV2Count(summary.highRiskAssetCount, locale),
            percent: percentageNumberFormat.format(highRiskPercentage),
          })}
        </Text>
        <Text size="md">
          <span
            className="mr-(--spacing-8) inline-block size-(--spacing-12) rounded-full bg-datavis-crit-green align-middle"
            aria-hidden="true"
          />
          {t("enterprise.assetsV2.notHighRiskLegend", {
            count: formatAssetsV2Count(summary.notHighRiskAssetCount, locale),
            percent: percentageNumberFormat.format(notHighRiskPercentage),
          })}
        </Text>
      </div>
    </Stack>
  );
}
